import type { FormEvent } from 'react';
import { createRef } from 'react';
import ReCaptcha from 'react-google-recaptcha';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { googleCaptchaSiteKey } from '@gonfalon/constants/constants';
import {
  enableGDPRComplianceCheckboxes,
  enableRemoveOrganizationFromSignup,
  duplicateSignupNotifyExistingAccount,
  enableSignupRefererForwarding,
  isCaptchaEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { useTypedAppForm } from '@gonfalon/forms-experimental/hooks';
import { NewTabLink } from '@gonfalon/launchpad-experimental/NewTabLink';
import { getDemoRequestUrl } from '@gonfalon/onboarding/utils';
import { isRESTAPIError } from '@gonfalon/rest-api/errors';
import { useCreateUnverifiedMember } from '@gonfalon/rest-api/queries/account';
import { Alert, Button, Form, Label, Link } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import type { TeamUsesSsoNavigationState } from '#components/loginAndSignUp/TeamUsesSsoContainer';
import { useRequiresGDPRCompliance } from '#hooks/useRequiresGDPRCompliance';
import { logger } from '#utils/flagExperimentUtils';
import { trackGDPRUserOptOut, trackLoginForm, trackSignUp } from '#utils/loginUtils';
import Logger from '#utils/logUtils';
import { clearStoredSignupLandingUrl } from '#utils/signupLandingUrlStorage';
import type { SignupOutcome } from '#utils/signupOutcome';
import type { SsoDomainSafeguardEmailSignupPayload } from '#utils/ssoDomainSafeguardSignupPayloadStorage';
import { persistableSsoDomainSafeguardEmailPayload } from '#utils/ssoDomainSafeguardSignupPayloadStorage';
import { getSignupTrialSubmitAnalyticsProps } from '#utils/tracking/signupEnterpriseDemo';

import { buildSignupCustomReferer, getForwardedReferer } from './agentUtmParams';
import { useSignUpHandlers } from './useSignUpHandlers';

import styles from './SignUpForm.module.css';

const signupLogger = Logger.get('SignUpForm');

const SSO_ENFORCED_ERROR_CODE = 'sso_enforced';
const TEAM_USES_SSO_PATH = '/team-uses-sso';

// isSsoEnforcedError reports whether the signup endpoint rejected the signup because the email
// domain maps to an SSO-enforced account, which means we should send the user to the standalone
// "your team uses SSO" page instead of creating an orphan account.
function isSsoEnforcedError(error: unknown): boolean {
  return isRESTAPIError(error) && error.code === SSO_ENFORCED_ERROR_CODE;
}

type SignUpFormProps = {
  hideDemoLink?: boolean;
};

export const SignUpForm = ({ hideDemoLink = false }: SignUpFormProps) => {
  const { requiresGDPRCompliance } = useRequiresGDPRCompliance();
  const enableGDPRCompliance = enableGDPRComplianceCheckboxes() && requiresGDPRCompliance;
  const onSuccessHandler = useSignUpHandlers();
  const createUnverifiedMember = useCreateUnverifiedMember();
  const showCaptcha = isCaptchaEnabled();
  const recaptchaRef = createRef<ReCaptcha>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { mutate, reset, isError, error, isPending } = createUnverifiedMember;
  const existingAccountErrorMessage = 'Please login with your existing account';
  const isExistingAccountError =
    !duplicateSignupNotifyExistingAccount() && error?.message === existingAccountErrorMessage;

  const requiredField = z.string().min(1, 'Error: This field is required');
  const FormSchema = z.object({
    firstName: requiredField,
    lastName: requiredField,
    username: requiredField.email('Error: This is not a valid email address'),
    password: z
      .string()
      .min(8, 'Error: This must be at least 8 characters')
      .refine(
        (val) => {
          const checks = [
            /[A-Z]/.test(val), // uppercase
            /[a-z]/.test(val), // lowercase
            /[0-9]/.test(val), // number
            /[^A-Za-z0-9]/.test(val), // special character
          ];
          return checks.filter(Boolean).length >= 3;
        },
        {
          message:
            'Error: This must contain at least three of the following: 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character',
        },
      ),
    organization: enableRemoveOrganizationFromSignup() ? z.string().optional() : requiredField,
    marketingEmails: z.boolean().optional(),
    acceptTerms: enableGDPRCompliance
      ? z.boolean().refine((value) => value, 'You must accept the terms and conditions')
      : z.boolean().optional(),
  });

  type FormData = z.infer<typeof FormSchema>;

  const submitForm = (data: FormData) => {
    const customReferer = enableSignupRefererForwarding()
      ? getForwardedReferer(searchParams)
      : buildSignupCustomReferer(searchParams);

    // Send the user to the standalone /team-uses-sso page. We reset the mutation first so the
    // generic error alert doesn't flash the SSO message before we leave.
    const goToTeamUsesSso = (signupPayload: SsoDomainSafeguardEmailSignupPayload) => {
      reset();
      clearStoredSignupLandingUrl();
      const persistablePayload = persistableSsoDomainSafeguardEmailPayload(signupPayload);
      const state: TeamUsesSsoNavigationState = { signupMethod: 'email', signupPayload: persistablePayload };
      void navigate(TEAM_USES_SSO_PATH, { state });
    };

    const body: FormData & { customReferer: string } = { ...data, customReferer };
    signupLogger.log(`signup: submitting form with customReferer (${customReferer})`);
    const payload = { body };

    if (enableGDPRCompliance && !data.marketingEmails) {
      trackGDPRUserOptOut({ component: 'SignUpForm', email: data.username });
    }
    trackLoginForm('App Signup Form Submitted', getSignupTrialSubmitAnalyticsProps());
    mutate(payload, {
      onSuccess: async (response) => {
        clearStoredSignupLandingUrl();
        const signupOutcome = (response as { signupOutcome?: SignupOutcome }).signupOutcome;
        await onSuccessHandler({ ...data, signupOutcome });
      },
      onError: (err) => {
        if (isSsoEnforcedError(err)) {
          goToTeamUsesSso(body);
          return;
        }
        logger.error(`Error creating unverified member: ${err}`);
      },
    });
  };

  const onSubmit = ({ value }: { value: FormData }) => {
    if (showCaptcha) {
      if (!recaptchaRef.current) {
        return;
      }

      recaptchaRef.current.reset();
      recaptchaRef.current.execute();
    } else {
      submitForm(value);
    }
  };

  const form = useTypedAppForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      acceptTerms: false,
      marketingEmails: false,
      password: '',
    },
    validators: {
      onChange: FormSchema,
      onSubmit: FormSchema,
      onMount: FormSchema,
    },
    onSubmit,
  });

  const { AppForm, AppField, Subscribe } = form;

  const handleCaptchaChange = (value: string | null) => {
    const { values } = form.state;
    const formWithCaptcha = { ...values, captcha: value || '' };

    submitForm(formWithCaptcha);
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    void form.handleSubmit();
  };

  return (
    <Form onSubmit={handleFormSubmit} className={styles.form}>
      <AppForm>
        {isError && (
          <Alert status="error">
            {isExistingAccountError ? (
              <span>
                Please <Link href="/login">login</Link> with your existing account
              </span>
            ) : (
              error?.message || 'An error occurred. Please try again.'
            )}
          </Alert>
        )}
        <div className={styles.name}>
          <div>
            <AppField name="firstName">{(field) => <field.TextInputField label="First name" isRequired />}</AppField>
          </div>
          <div>
            <AppField name="lastName">{(field) => <field.TextInputField label="Last name" isRequired />}</AppField>
          </div>
        </div>
        <div className={styles.username}>
          <AppField name="username">
            {(field) => <field.TextInputField label="Email" type="email" isRequired />}
          </AppField>
        </div>
        {!enableRemoveOrganizationFromSignup() && (
          <div className={styles.organization}>
            <AppField name="organization">
              {(field) => <field.TextInputField label="Organization" name="organization" />}
            </AppField>
          </div>
        )}
        <div className={styles.password}>
          <AppField name="password">
            {(field) => (
              <field.TextInputField label="Password" type="password" isRequired autoComplete="new-password" />
            )}
          </AppField>
        </div>
        {enableGDPRCompliance && (
          <div className={styles.checkboxes}>
            <AppField name="acceptTerms">
              {(field) => (
                <field.Checkbox>
                  <Label>
                    Accept the{' '}
                    <NewTabLink
                      href="https://launchdarkly.com/policies/subscription-terms/"
                      text="terms and conditions"
                      showIcon={false}
                    />
                  </Label>
                </field.Checkbox>
              )}
            </AppField>
            <AppField name="marketingEmails">
              {(field) => (
                <field.Checkbox>
                  <Label>(Optional) Get occasional emails about new features and events</Label>
                </field.Checkbox>
              )}
            </AppField>
          </div>
        )}
        <div className={styles.submit}>
          <Subscribe
            selector={(state) => {
              return {
                isValid: state.isValid,
                isSubmitting: state.isSubmitting,
              };
            }}
          >
            {({ isValid, isSubmitting }: { isValid: boolean; isSubmitting: boolean }) => (
              <Button
                variant="primary"
                isDisabled={!isValid || isPending || isSubmitting}
                className={styles.submitButton}
                type="submit"
                isPending={isPending || isSubmitting}
              >
                {isPending || isSubmitting ? 'Submitting...' : 'Start my 14-day trial now'}
              </Button>
            )}
          </Subscribe>
          {!hideDemoLink && (
            <>
              or
              <Link
                href={getDemoRequestUrl()}
                className={styles.demoLink}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  trackSignUp('CTA Link Clicked', {
                    component: 'SignUpForm',
                    ctaText: 'Get a live demo',
                    url: pathname,
                  })
                }
              >
                Get a live demo
                <Icon className={styles.arrowIcon} name="arrow-right-thin" />
              </Link>
            </>
          )}
        </div>
        {showCaptcha && (
          <ReCaptcha
            ref={recaptchaRef}
            sitekey={googleCaptchaSiteKey()}
            size="invisible"
            onChange={handleCaptchaChange}
          />
        )}
      </AppForm>
    </Form>
  );
};
